<template>
  <div>
    <v-app-bar v-if="mobile" color="#0a1628" elevation="0" class="mobile-app-bar">
      <v-app-bar-nav-icon color="white" @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-img src="/logo.png" max-width="100" contain style="cursor: pointer" @click="$router.push('/')" />
      <v-spacer />
      <v-btn icon size="small" color="white" @click="logout"><v-icon>mdi-logout</v-icon></v-btn>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      :permanent="!mobile"
      :temporary="mobile"
      location="left"
      width="280"
      class="drawer-custom"
      color="#0a1628"
      :border="0"
    >
      <div class="drawer-inner">
        <div class="px-5 py-6">
          <v-img src="/logo.png" max-width="110" contain style="cursor: pointer" @click="$router.push('/')" />
        </div>

        <div v-if="$user?.id" class="user-card mx-4 mb-4 pa-3">
          <div class="d-flex align-center ga-3">
            <v-avatar size="34" class="user-avatar">
              <v-img
                v-if="$user.id"
                :src="`/api/employees/${$user.id}/image`"
                :alt="`${$user.firstname} ${$user.lastname}`"
                cover
              >
                <template #error>
                  <span class="text-caption font-weight-bold" style="color: #0a1628">{{ initials }}</span>
                </template>
              </v-img>
              <span v-else class="text-caption font-weight-bold" style="color: #0a1628">{{ initials }}</span>
            </v-avatar>
            <div style="min-width: 0; flex: 1">
              <div class="text-white text-body-2 font-weight-medium text-truncate">
                {{ $user.firstname }} {{ $user.lastname }}
              </div>
              <div class="text-caption text-truncate role-label">Administrator</div>
            </div>
          </div>
        </div>

        <div class="nav-section px-3">
          <div class="nav-label">Menü</div>
          <v-list nav density="compact" class="pa-0">
            <v-list-item
              v-for="item in mainMenuItems"
              :key="item.path"
              :to="item.path"
              :active="isActive(item.path)"
              class="nav-item"
              @click="mobile && (drawer = false)"
            >
              <template #prepend>
                <div class="nav-icon-wrap" :class="{ 'nav-icon-active': isActive(item.path) }">
                  <v-icon size="18">{{ item.icon }}</v-icon>
                </div>
              </template>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>

        <div class="nav-section px-3">
          <div class="nav-label">Administration</div>
          <v-list nav density="compact" class="pa-0">
            <v-list-item
              v-for="item in adminMenuItems"
              :key="item.path"
              :to="item.path"
              :active="isActive(item.path)"
              class="nav-item"
              @click="mobile && (drawer = false)"
            >
              <template #prepend>
                <div class="nav-icon-wrap nav-icon-admin" :class="{ 'nav-icon-admin-active': isActive(item.path) }">
                  <v-icon size="18">{{ item.icon }}</v-icon>
                </div>
              </template>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </div>

      <template #append>
        <div class="px-3 pb-4">
          <div class="logout-btn" @click="logout">
            <v-icon size="18" style="color: rgba(255,255,255,0.4)">mdi-logout</v-icon>
            <span>{{ loggingOut ? 'Abmelden…' : 'Abmelden' }}</span>
          </div>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main class="bg-main">
      <div class="content-wrapper pa-4">
        <keep-alive><router-view /></keep-alive>
      </div>
    </v-main>
  </div>
</template>

<script>
import axios from "axios";
import { useUserStore } from "@/store/index.js";

export default {
  name: "Navigation",

  data: () => ({
    drawer: true,
    mobile: false,
    loggingOut: false,
    allMainMenuItems: [
      { title: "Dashboard",     icon: "mdi-view-dashboard-outline", path: "/dashboard", perm: "dashboard.view" },
      { title: "Sections",      icon: "mdi-vector-intersection",    path: "/sections",  perm: "sections.view" },
      { title: "Kategorien",    icon: "mdi-format-list-bulleted",   path: "/categories", perm: "categories.view" },
      { title: "Pipeline",      icon: "mdi-pipe",                   path: "/pipeline",  perm: "pipeline.view" },
      { title: "Reviews",       icon: "mdi-clipboard-check-outline", path: "/reviews",  perm: "reviews.view" },
      { title: "Tasks & Bugs",  icon: "mdi-bug-check-outline",       path: "/tasks",    perm: "tasks.view" },
      { title: "Anleitungen",   icon: "mdi-book-open-page-variant-outline", path: "/anleitungen", perm: "guides.view" },
      { title: "Support",       icon: "mdi-help-circle-outline",    path: "/support",   perm: "support.view" },
    ],
    allAdminMenuItems: [
      { title: "Benutzerkonten", icon: "mdi-account-group-outline", path: "/employees",    perm: "employees.view" },
      { title: "Integrationen",  icon: "mdi-connection",            path: "/integrations", perm: "integrations.view" },
      { title: "Einstellungen",  icon: "mdi-cog-outline",           path: "/settings",     perm: "settings.view" },
    ],
  }),

  computed: {
    initials() {
      const u = this.$user;
      if (!u) return "";
      return ((u.firstname?.charAt(0) || "") + (u.lastname?.charAt(0) || "")).toUpperCase();
    },
    mainMenuItems() {
      return this.allMainMenuItems.filter((i) => !i.perm || this.$can(i.perm));
    },
    adminMenuItems() {
      return this.allAdminMenuItems.filter((i) => !i.perm || this.$can(i.perm));
    },
  },

  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobile);
  },

  methods: {
    checkMobile() {
      this.mobile = window.innerWidth < 960;
      this.drawer = !this.mobile;
    },
    isActive(path) {
      return this.$route.path === path || this.$route.path.startsWith(path + "/");
    },
    async logout() {
      this.loggingOut = true;
      try {
        await axios.post("/api/authorization/logout");
      } catch (e) {
        console.error("Logout-Fehler:", e);
      } finally {
        useUserStore().setUser({
          id: null, firstname: "", lastname: "", email: "",
          state: null, position: null,
          isTechnicalReviewer: 0, isVisualReviewer: 0,
          permissions: {},
        });
        this.loggingOut = false;
        this.$router.push({ name: "Login" });
      }
    },
  },
};
</script>

<style scoped>
:deep(.v-application__wrap) { height: 100vh !important; overflow: hidden !important; }
.bg-main { background-color: #f8f9fb !important; height: 100vh !important; overflow-y: auto !important; }
:deep(.v-navigation-drawer) { position: fixed !important; left: 0 !important; overflow-y: auto !important; }
.drawer-custom { border-right: 1px solid rgba(255,255,255,0.06) !important; box-shadow: none !important; }
.drawer-custom :deep(.v-navigation-drawer__border) { display: none !important; }
.drawer-inner { display: flex; flex-direction: column; }
.mobile-app-bar { border-bottom: 1px solid rgba(255,255,255,0.06) !important; }

.user-card {
  background: rgba(255,255,255,0.05);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.06);
}
.user-avatar { background: #e8eaed !important; }
.role-label { color: #B2F4AA; font-size: 11px; }

.nav-section { margin-bottom: 8px; }
.nav-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.25);
  padding: 16px 12px 8px;
}

.nav-item {
  min-height: 40px !important;
  border-radius: 10px !important;
  margin-bottom: 2px !important;
  color: rgba(255,255,255,0.55) !important;
  padding-left: 8px !important;
  padding-right: 12px !important;
  transition: all 0.15s ease !important;
}
.nav-item :deep(.v-list-item__prepend) { width: auto !important; min-width: auto !important; margin-right: 12px !important; }
.nav-item :deep(.v-list-item-title) { font-size: 13.5px !important; font-weight: 500 !important; color: rgba(255,255,255,0.55) !important; }

.nav-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.05);
  transition: all 0.15s;
}
.nav-icon-wrap .v-icon { color: rgba(255,255,255,0.45) !important; }
.nav-icon-active { background: rgba(59,130,246,0.2) !important; }
.nav-icon-active .v-icon { color: #60a5fa !important; }

.nav-icon-admin { background: rgba(178,244,170,0.12); }
.nav-icon-admin .v-icon { color: #B2F4AA !important; }
.nav-icon-admin-active { background: rgba(178,244,170,0.2) !important; }

.v-list-item--active { background: rgba(255,255,255,0.08) !important; }
.v-list-item--active :deep(.v-list-item__overlay) { opacity: 0 !important; }
.v-list-item--active :deep(.v-list-item-title) { color: white !important; font-weight: 600 !important; }
.nav-item:hover:not(.v-list-item--active) { background: rgba(255,255,255,0.04) !important; }
.nav-item:hover:not(.v-list-item--active) :deep(.v-list-item__overlay) { opacity: 0 !important; }

.logout-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(255,255,255,0.35);
  border: 1px solid rgba(255,255,255,0.06);
}
.logout-btn:hover {
  background: rgba(239,68,68,0.08);
  border-color: rgba(239,68,68,0.15);
  color: #fca5a5;
}
.logout-btn:hover .v-icon { color: #fca5a5 !important; }

.content-wrapper { width: 100%; min-height: 100%; }

@media (max-width: 959px) {
  .content-wrapper { padding: 12px !important; }
}
</style>
